
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle, Calendar, Timer } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";

interface CampaignAutomationStatusProps {
  status: "draft" | "scheduled" | "active" | "completed" | "processing_grace_period";
  startDate: string;
  endDate: string;
  gracePeriodDays?: number;
}

const CampaignAutomationStatus = ({ 
  status, 
  startDate, 
  endDate, 
  gracePeriodDays = 7 
}: CampaignAutomationStatusProps) => {
  const today = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  const graceEndDate = new Date(end.getTime() + (gracePeriodDays * 24 * 60 * 60 * 1000));
  
  const daysToStart = differenceInDays(start, today);
  const daysToEnd = differenceInDays(end, today);
  const daysToGraceEnd = differenceInDays(graceEndDate, today);

  const getStatusInfo = () => {
    switch (status) {
      case "draft":
        return {
          icon: <Clock className="h-3 w-3" />,
          label: "Rascunho",
          variant: "secondary" as const,
          description: "Campanha ainda não foi publicada"
        };
      case "scheduled":
        return {
          icon: <Calendar className="h-3 w-3" />,
          label: `Agendada (${daysToStart > 0 ? `${daysToStart} dias` : 'inicia hoje'})`,
          variant: "outline" as const,
          description: daysToStart > 0 
            ? `Será ativada automaticamente em ${format(start, "dd/MM/yyyy", { locale: ptBR })}`
            : "Será ativada automaticamente hoje"
        };
      case "active":
        return {
          icon: <CheckCircle className="h-3 w-3" />,
          label: `Ativa (${daysToEnd > 0 ? `${daysToEnd} dias restantes` : 'finaliza hoje'})`,
          variant: "default" as const,
          description: daysToEnd > 0 
            ? `Será finalizada automaticamente em ${format(end, "dd/MM/yyyy", { locale: ptBR })}`
            : "Será finalizada automaticamente hoje"
        };
      case "processing_grace_period":
        return {
          icon: <Timer className="h-3 w-3" />,
          label: `Processando (${daysToGraceEnd > 0 ? `${daysToGraceEnd} dias restantes` : 'finaliza hoje'})`,
          variant: "secondary" as const,
          description: daysToGraceEnd > 0 
            ? `Aceitando pedidos feitos durante a campanha. Será finalizada em ${format(graceEndDate, "dd/MM/yyyy", { locale: ptBR })}`
            : "Período de processamento finaliza hoje"
        };
      case "completed":
        return {
          icon: <CheckCircle className="h-3 w-3" />,
          label: "Finalizada",
          variant: "secondary" as const,
          description: `Finalizada em ${format(end, "dd/MM/yyyy", { locale: ptBR })}`
        };
      default:
        return {
          icon: <Clock className="h-3 w-3" />,
          label: status,
          variant: "secondary" as const,
          description: ""
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="flex flex-col space-y-1">
      <Badge variant={statusInfo.variant} className="w-fit">
        {statusInfo.icon}
        <span className="ml-1">{statusInfo.label}</span>
      </Badge>
      {statusInfo.description && (
        <span className="text-xs text-muted-foreground">
          {statusInfo.description}
        </span>
      )}
    </div>
  );
};

export default CampaignAutomationStatus;
